import {
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type {
  Source,
  Title,
  TitleSource,
} from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

interface TitleSourceRow extends TitleSource {
  sources: Source
}

interface NewTitleSource {
  source_id: string
  external_url: string
}

const EMPTY_NEW: NewTitleSource = {
  source_id: '',
  external_url: '',
}

export default function TitleSourcesPage() {
  const { id: titleId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [titleName, setTitleName] = useState('')
  const [sources, setSources] = useState<Source[]>([])
  const [titleSources, setTitleSources] = useState<TitleSourceRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [newEntry, setNewEntry] = useState<NewTitleSource>(EMPTY_NEW)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!titleId) return
    const resolvedTitleId = titleId
    async function load() {
      const [titleRes, sourcesRes, titleSourcesRes] = await Promise.all([
        supabase
          .from('titles')
          .select('canonical_name')
          .eq('id', resolvedTitleId)
          .single(),
        supabase.from('sources').select('*').order('name'),
        supabase
          .from('title_sources')
          .select('*, sources(*)')
          .eq('title_id', resolvedTitleId)
          .order('created_at', { ascending: false }),
      ])
      setTitleName((titleRes.data as Title | null)?.canonical_name ?? '')
      setSources(sourcesRes.data ?? [])
      setTitleSources((titleSourcesRes.data ?? []) as TitleSourceRow[])
      if (sourcesRes.data?.length) {
        setNewEntry((prev) => ({ ...prev, source_id: sourcesRes.data[0].id }))
      }
      setIsLoading(false)
    }
    load()
  }, [titleId])

  async function handleAdd() {
    setError(null)
    if (!newEntry.source_id || !newEntry.external_url.trim()) {
      setError('Source and external URL are required.')
      return
    }
    if (!titleId) return
    setSaving(true)
    const { data, error: err } = await supabase
      .from('title_sources')
      .insert({
        title_id: titleId,
        source_id: newEntry.source_id,
        external_url: newEntry.external_url.trim(),
      })
      .select('*, sources(*)')
      .single()

    if (err) {
      setError(err.message)
      setSaving(false)
      return
    }
    setTitleSources((prev) => [data as TitleSourceRow, ...prev])
    setNewEntry({ ...EMPTY_NEW, source_id: newEntry.source_id })
    setIsAdding(false)
    setSaving(false)
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this source link?')) return
    await supabase.from('title_sources').delete().eq('id', id)
    setTitleSources((prev) => prev.filter((ts) => ts.id !== id))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/admin/manga')}
        className="mb-4 inline-flex items-center gap-1 text-gray-500 text-sm hover:text-gray-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to titles
      </button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-bold text-gray-900 text-xl">Source Links</h1>
          <p className="text-gray-500 text-sm">{titleName}</p>
        </div>
        {!isAdding && (
          <button
            type="button"
            onClick={() => {
              setIsAdding(true)
              setError(null)
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add source link
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-red-600 text-sm">
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Source
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                External URL
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isAdding && (
              <tr className="bg-blue-50">
                <td className="px-4 py-2">
                  <select
                    value={newEntry.source_id}
                    title="Select source"
                    onChange={(e) =>
                      setNewEntry((p) => ({ ...p, source_id: e.target.value }))
                    }
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                  >
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="url"
                    value={newEntry.external_url}
                    onChange={(e) =>
                      setNewEntry((p) => ({
                        ...p,
                        external_url: e.target.value,
                      }))
                    }
                    placeholder="https://..."
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={saving}
                      title="Save"
                      className="rounded p-1 text-green-600 hover:bg-green-50"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false)
                        setError(null)
                      }}
                      title="Cancel"
                      className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {titleSources.length === 0 && !isAdding && (
              <tr>
                <td colSpan={3} className="py-10 text-center text-gray-400">
                  No source links yet.
                </td>
              </tr>
            )}
            {titleSources.map((ts) => (
              <tr key={ts.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">
                  {ts.sources.name}
                </td>
                <td className="max-w-xs px-4 py-2">
                  <a
                    href={ts.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-blue-600 hover:underline"
                  >
                    {ts.external_url}
                  </a>
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDelete(ts.id)}
                      title="Delete"
                      className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
